/* ==========================================================================
   CO Nav — Charlie Oven navigation behaviour
   Build theme 158404018408. Loaded deferred from sections/header.liquid.

   Two jobs:
     1. Accessibility the theme does not ship — aria-expanded, Escape-to-close
        with focus return, close-on-focus-leave, hover intent, touch handling.
     2. Analytics — dataLayer events for GTM container GTM-NQN8JTTC.

   DELIBERATELY STANDALONE. An earlier draft (theme 158264197352) achieved the
   same by forking assets/menu-dropdown.js. This does not: it observes the
   theme's own state from outside via MutationObserver, so a Be Yours update
   that rewrites menu-dropdown.js cannot silently drop our a11y work, and the
   diff against stock stays at zero for that file.

   No libraries. No globals beyond the IIFE.
   ========================================================================== */

(function () {
  'use strict';

  var DESKTOP_MQ = window.matchMedia('(min-width: 990px)');
  var COARSE_MQ = window.matchMedia('(hover: none)');
  var REDUCED_MQ = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Nav items the brief asks to track by name. Matched on the trimmed,
     lower-cased label so a capitalisation change in Admin does not silently
     stop the tracking. */
  var TRACKED = [
    'ovens',
    'outdoor kitchens',
    'accessories',
    'design your kitchen',
    'book a demo'
  ];

  function push(payload) {
    /* GTM is loaded in layout/theme.liquid with Consent Mode v2 already
       wired, so pushes inherit the user's cookie choice. If the container is
       ever removed this queues harmlessly on a plain array. */
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  function labelOf(el) {
    return (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  /* ------------------------------------------------------------------------
     1. DESKTOP DROPDOWNS
     The theme toggles .is-active on the <dropdown-menu> / mega wrapper. We
     mirror that onto aria-expanded rather than trying to own the state.
     ------------------------------------------------------------------------ */

  function wireDesktopItem(item) {
    var panel = item.querySelector('.list-menu-dropdown');
    var trigger = item.querySelector(':scope > a, :scope > .header__menu-item--top');
    if (!panel || !trigger) return;

    trigger.setAttribute('aria-expanded', 'false');
    if (!panel.id) {
      panel.id = 'co-nav-panel-' + Math.random().toString(36).slice(2, 9);
    }
    trigger.setAttribute('aria-controls', panel.id);

    var isOpen = function () {
      return item.classList.contains('is-active');
    };

    var close = function () {
      if (typeof item.close === 'function') {
        item.close();
      } else {
        item.classList.remove('is-active');
      }
    };

    /* Mirror the theme's own class onto aria-expanded, and fire the
       dropdown-open event exactly once per open. */
    var observer = new MutationObserver(function () {
      var open = isOpen();
      var was = trigger.getAttribute('aria-expanded') === 'true';
      if (open === was) return;
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        push({
          event: 'co_nav_dropdown_open',
          nav_item: labelOf(trigger),
          nav_device: 'desktop'
        });
      }
    });
    observer.observe(item, { attributes: true, attributeFilter: ['class'] });

    /* Escape closes and returns focus to the trigger — without the focus
       return, a keyboard user is dumped at the top of the document. */
    item.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      if (!isOpen()) return;
      event.stopPropagation();
      close();
      trigger.focus();
    });

    /* Tabbing out of the panel closes it. rAF so document.activeElement has
       settled before we test containment. */
    item.addEventListener('focusout', function () {
      requestAnimationFrame(function () {
        if (isOpen() && !item.contains(document.activeElement)) close();
      });
    });

    /* Touch: first tap opens the panel instead of navigating away, second tap
       follows the link. Pointer devices are untouched. */
    trigger.addEventListener('click', function (event) {
      if (!COARSE_MQ.matches) return;
      if (isOpen()) return;
      event.preventDefault();
      item.classList.add('is-active');
    });
  }

  /* ------------------------------------------------------------------------
     2. HOVER INTENT
     Opening on a pointer that is merely passing through makes a calm header
     feel twitchy. 120ms of dwell, then open; an already-open header opens the
     next panel instantly so lateral movement along the nav stays fluid.
     ------------------------------------------------------------------------ */

  function wireHoverIntent(item) {
    if (!item.querySelector('.list-menu-dropdown')) return;
    var timer = null;

    item.addEventListener('mouseenter', function () {
      if (!DESKTOP_MQ.matches || COARSE_MQ.matches) return;
      var anyOpen = document.querySelector('.header__inline-menu .is-active');
      var delay = anyOpen ? 0 : 120;
      clearTimeout(timer);
      timer = setTimeout(function () {
        item.classList.add('is-active');
      }, delay);
    });

    item.addEventListener('mouseleave', function () {
      clearTimeout(timer);
    });
  }

  /* ------------------------------------------------------------------------
     3. MOBILE DRAWER
     CSS turns the theme's slide-over panels into an accordion; this adds the
     ARIA the <details>/<summary> pair does not express on its own, and
     reports opens as mobile rather than desktop.
     ------------------------------------------------------------------------ */

  function wireDrawer(root) {
    var sections = root.querySelectorAll(
      '.menu-drawer__navigation > .menu-drawer__menu > li > details'
    );

    Array.prototype.forEach.call(sections, function (details) {
      var summary = details.querySelector(':scope > summary');
      if (!summary) return;

      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');

      details.addEventListener('toggle', function () {
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
        if (!details.open) return;

        push({
          event: 'co_nav_dropdown_open',
          nav_item: labelOf(summary),
          nav_device: 'mobile'
        });

        /* One section open at a time. Four accordions all expanded turns the
           drawer back into the long scrolling list the brief is trying to get
           rid of, and pushes Book a Demo below the fold. */
        Array.prototype.forEach.call(sections, function (other) {
          if (other !== details) other.open = false;
        });
      });
    });

    /* Escape closes the whole drawer, matching the desktop contract. */
    root.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      var container = root.closest('details.menu-drawer-container');
      if (!container || !container.open) return;
      var toggle = container.querySelector(':scope > summary');
      container.open = false;
      if (toggle) toggle.focus();
    });
  }

  /* ------------------------------------------------------------------------
     4. CLICK TRACKING
     One delegated listener for the whole header rather than a listener per
     link. nav_level distinguishes a top-level click from a click inside an
     open panel, so "opened Ovens then bounced" is separable from "opened
     Ovens and went to the Cheeky".
     ------------------------------------------------------------------------ */

  function wireTracking(scope, device) {
    scope.addEventListener(
      'click',
      function (event) {
        var link = event.target.closest('a');
        if (!link || !scope.contains(link)) return;

        var label = labelOf(link);
        var isCta =
          link.classList.contains('co-header-cta') ||
          link.classList.contains('co-drawer-cta');

        if (TRACKED.indexOf(label) === -1 && !isCta) return;

        var inPanel = !!link.closest('.list-menu-dropdown, .menu-drawer__submenu');

        push({
          event: isCta ? 'co_nav_cta_click' : 'co_nav_click',
          nav_item: label,
          nav_level: inPanel ? 'child' : 'top',
          nav_device: device,
          nav_destination: link.getAttribute('href') || ''
        });
      },
      true
    );
  }

  /* ------------------------------------------------------------------------
     5. INIT
     ------------------------------------------------------------------------ */

  function init() {
    var header = document.querySelector('.shopify-section-header');
    if (!header) return;

    var items = header.querySelectorAll('.header__inline-menu > ul > li');
    Array.prototype.forEach.call(items, function (item) {
      var target =
        item.querySelector(':scope > dropdown-menu, :scope > mega-menu') || item;
      wireDesktopItem(target);
      if (!REDUCED_MQ.matches) wireHoverIntent(target);
    });

    var drawer = header.querySelector('.menu-drawer');
    if (drawer) wireDrawer(drawer);

    wireTracking(header, DESKTOP_MQ.matches ? 'desktop' : 'mobile');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
